import { Maximize2, Minimize2, X } from "lucide-react";
import { activePanel, type DockSlot, useDockStore } from "../../stores/dock";
import { DockTabs } from "./DockTabs";
import { DOCK_KINDS, type DockKindMap, resolveDockKind } from "./registry";
import styles from "./DockCard.module.css";

/**
 * One dock card (2026-07-15): a rounded, self-contained panel holding N tabs of
 * any kind, with its own chrome. `DockColumn` stacks these.
 *
 * The card is KIND-BLIND by construction — it looks its panels' kinds up in the
 * registry and renders whatever comes back. It never branches on `kind`, and it
 * never reads `panel.data`. That is the whole point of the seam: adding the
 * terminal must not edit this file.
 *
 * Chrome is ONE row — tabs left, controls right — matching the reference: the
 * cards are small, and a second full-width header row on each would eat the
 * column alive once three are stacked. The tab already carries the title, so a
 * separate title line would only repeat it.
 */

interface DockCardProps {
	slot: DockSlot;
	/** Injectable for tests. Production always uses the real registry. */
	kinds?: DockKindMap;
	/** Grab-and-move. The card renders the affordance; the COLUMN owns the drag,
	 *  because only it knows where the other cards are. Absent = not draggable
	 *  (a lone card has nowhere to go, and an affordance that does nothing is
	 *  worse than none). */
	onGrab?: (e: React.PointerEvent) => void;
	/** This card is the one being dragged — dimmed so the drop position reads. */
	dragging?: boolean;
}

export function DockCard({ slot, kinds = DOCK_KINDS, onGrab, dragging }: DockCardProps) {
	const setActive = useDockStore((s) => s.setActive);
	const closePanel = useDockStore((s) => s.closePanel);
	const closeSlot = useDockStore((s) => s.closeSlot);
	const toggleMaximize = useDockStore((s) => s.toggleMaximize);
	const maximizedSlotId = useDockStore((s) => s.maximizedSlotId);

	const panel = activePanel(slot);
	const def = panel ? resolveDockKind(panel.kind, kinds) : null;
	const isMax = maximizedSlotId === slot.id;

	return (
		<section
			className={`${styles.card} ${dragging ? styles.cardDragging : ""}`}
			// The tab label alone can be "index.ts" three cards over; naming the
			// card by what's IN it is what makes the region announce usefully.
			aria-label={panel ? `${panel.title} panel` : "Panel"}
		>
			{/* Grab handle, top-centre. Sits between the tab strip (left) and the
			    controls (right), which is the one horizontally-central spot on the
			    chrome row that belongs to the CARD rather than to anything in it —
			    grabbing a tab would mean moving the tab, not the card. */}
			{onGrab && (
				<div
					className={styles.grab}
					onPointerDown={onGrab}
					role="button"
					tabIndex={-1}
					aria-label={panel ? `Move ${panel.title} panel` : "Move panel"}
					title="Drag to move this panel"
				>
					<span className={styles.grabBar} aria-hidden="true" />
				</div>
			)}
			<div className={styles.chrome}>
				<DockTabs
					tabs={slot.panels.map((p) => ({
						id: p.id,
						title: p.title,
						icon: resolveDockKind(p.kind, kinds)?.tabIcon(p),
					}))}
					activeId={slot.activeId}
					onActivate={setActive}
					onClose={closePanel}
					// Every card is its own tablist; a screen reader hitting three
					// strips all called "Open panels" cannot tell them apart.
					label={panel ? `Tabs in ${panel.title} panel` : "Panel tabs"}
					className={styles.chromeTabs}
				/>
				<div className={styles.controls}>
					{def?.Toolbar && panel && <def.Toolbar panel={panel} />}
					<button
						type="button"
						className={styles.iconBtn}
						onClick={() => toggleMaximize(slot.id)}
						title={isMax ? "Restore" : "Expand"}
						aria-label={isMax ? "Restore panel" : "Expand panel"}
						aria-pressed={isMax}
					>
						{isMax ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
					</button>
					<button
						type="button"
						className={styles.iconBtn}
						onClick={() => closeSlot(slot.id)}
						// Says what it does: this discards every tab in the card, unlike
						// the dock's own ✕, which only hides.
						title="Close panel"
						aria-label={panel ? `Close ${panel.title} panel` : "Close panel"}
					>
						<X size={15} />
					</button>
				</div>
			</div>

			<div className={styles.body}>
				{!panel ? null : def ? (
					<def.Body panel={panel} />
				) : (
					// An unregistered kind is reachable for real: a persisted layout
					// naming a kind this build doesn't ship. Saying so beats an empty
					// card, which reads as a bug with no clue in it.
					<p className={styles.unavailable}>
						This panel type ({panel.kind}) isn’t available in this build.
					</p>
				)}
			</div>
		</section>
	);
}
